import React, {
  Suspense,
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Stage, MeshReflectorMaterial } from "@react-three/drei";
import { useLocation } from "react-router-dom";
import {
  Vector2,
  Vector3,
  WebGLRenderTarget,
  PCFSoftShadowMap,
  SRGBColorSpace,
  ACESFilmicToneMapping,
  UnsignedByteType,
  RGBAFormat,
  LinearFilter,
  Color
} from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { GammaCorrectionShader } from "three/examples/jsm/shaders/GammaCorrectionShader.js";
import { SMAAPass } from "three/examples/jsm/postprocessing/SMAAPass.js";

import "../../../index.css";
import styles from "../../../styles/ModelViewer/ProjectModelView.module.css";
import { getHdr } from "../HDRConstant.js";
import playIcon from "../../../assets/icon/play_icon.svg";
import LoadingScreen from "./LoadingScreen.js";
import ErrorScreen from "./ErrorScreen.js";
import ModelLight from "./ModelLight.js";
import { useModelLoader } from "./ThreeJSViewer.js";

// Componente principale che gestisce la visualizzazione 3D e AR
const ThreeJSViewerLight = forwardRef(function ThreeJSViewerLight(props, ref) {
  // Estrazione delle props e degli stati globali/di routing
  const project = props.project;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fovParam = +queryParams.get("fov");
  const mobileFov = +queryParams.get("fovMobile") || 50;

  //   // Stati locali per modalità, animazioni, AR, info modello, ecc.
  //   const [hasAnimations, setHasAnimations] = useState(false); // Il modello ha animazioni
  //   const [isAnimatedOn, setIsAnimatedOn] = useState(false); // Animazione in corso
  //   const [isFirstAnimation, setIsFirstAnimation] = useState(false); // Prima animazione
  //   const [animationTitle, setAnimationTitle] = useState(""); // Titolo animazione
  //   const [animationDuration, setAnimationDuration] = useState(0); // Durata animazione

  // Ref per accedere a oggetti Three.js (renderer, scena, camera)
  const glRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const composerRef = useRef(null);
  const smaaRef = useRef(null);
  const ssaaRef = useRef(2.0); // try 2.0 for still quality; 1.5 if too heavy

  useImperativeHandle(ref, () => ({
    getScene: () => sceneRef.current,
    onRender: (filename, width, height, options = {}) =>
      handleRenderClick(filename, width, height, options),
  }));

  // Stato per info geometriche del modello (dimensione e centro)
  const [modelInfo, setModelInfo] = useState({
    sizes: { x: 0, y: 0, z: 0 },
    center: { x: 0, y: 0, z: 0 },
  });

  // Recupero HDR e parametri di default
  const hdrNum = project?.hdr;
  const hdrPath = getHdr(hdrNum);

  const DEG_2_RAD = Math.PI / 180;
  const comeBackTitle = "Come Back";
  const LIGHT_INTESITY_FACTOR = 0.15;

  // Determinazione dell'URL del modello da caricare
  const modelUrl = props.singleSrc;
  // const modelUrl = 'https://firebasestorage.googleapis.com/v0/b/arshades-7e18a.appspot.com/o/glasses_data%2FMBoeOVaMPMe4qgLJIKfP%2Ftest_test_0HhrBnfie7sAUO88KHNN%2FTEST%202_test_test_complete.glb?alt=media&token=2c3d0ca2-d1ca-4681-9690-63c877241894';

  // Hook personalizzato per il caricamento del modello
  const {
    error,
    modelUrl: loadedModelUrl,
    isLoaded,
    loadingProgress,
    setError,
  } = useModelLoader(modelUrl);
  if (props.isRenderer && error) {
    console.error("Errore durante il caricamento del modello:", error.message);
  } else if (props.isRenderer && !error && loadedModelUrl) {
    console.log("Modello caricato con successo:", loadedModelUrl);
  }

  // Parametri per la scena 3D
  const SCENE_PARAMS = {
    src: project?.url ? project?.url : loadedModelUrl,
    alt: project?.alt,
    cameraControls: project?.cameraControls ?? true,
    autoRotate: project?.autoRotate ?? false,
    disableZoom: project?.disableZoom ?? false,
    lightExposition: project?.lightExposition ?? 1,
    shadowSmooth: project?.shadowSmooth ?? 0.3,
    shadowIntensity: project?.shadowIntensity ?? 0.5,
    hdr: hdrPath === 0 ? getHdr(8) : hdrPath,
    ar: "true",
    backgroundImage: project?.backgroundImage,
    skybox: project?.transparent,
    fieldOfView: fovParam
      ? fovParam
      : project?.fieldOfView
      ? +project?.fieldOfView.replace("deg", "")
      : 23,
    backgroundColor: !project?.transparent
      ? project?.backgroundColor ?? "#ffffff"
      : "#ffffff",
    isBackgroundImage: project?.isBackgroundImage === "true",
    title: project?.modelTitle ?? false,
    exposure: project?.lightExposition ?? "1.0",
    cameraOrbit: project?.cameraOrbit ?? "",
    useReflect: queryParams.get("reflect") === "true",
    useNoShadows: queryParams.get("noShadows") === "true",
    modelMinY: modelInfo ? modelInfo.center.y - modelInfo.sizes.y / 2 : 0,
  };

  // Effetto per inizializzare l'effect composer per il post-processing dei render
  useEffect(() => {
    const gl = glRef.current;
    const scene = sceneRef.current;
    const camera = cameraRef.current;

    if (gl && scene && camera) {
      // Inizializzazione dell'effect composer
      // if (props.isRenderer) {
      //   console.log("Effect Composer initialization...");
      //   console.log("WebGL capabilities:", JSON.stringify(gl.capabilities));
      //   console.log("Pixel ratio:", gl.getPixelRatio());
      // }

      // 1) Capabilities
      const isWebGL2 = gl.capabilities.isWebGL2 === true;
      const maxSamples = isWebGL2 ? (gl.capabilities.maxSamples ?? 0) : 0;
      console.log("maxSamples", maxSamples);

      // 2) Supersampling (not mandatory but recommended in software)
      //    Set 1.0, 1.5 or 2.0 based on CPU/time budget.
      const SSAA = ssaaRef.current;

      gl.setPixelRatio(1);
      const base = gl.getSize(new Vector2());
      const w = Math.floor(base.x * SSAA);
      const h = Math.floor(base.y * SSAA);

      // 3) MSAA if possible
      const useMSAA = isWebGL2 && maxSamples > 0;
      const samples = useMSAA ? Math.min(4, maxSamples) : 0;

      // Render target
      const renderTarget = new WebGLRenderTarget(w, h, {
        samples,
        format: RGBAFormat,
        type: UnsignedByteType,
        minFilter: LinearFilter,
        magFilter: LinearFilter,
        stencilBuffer: false,
        depthBuffer: true
      });

      // Effect Composer
      const effectComposer = new EffectComposer(gl, renderTarget);
      effectComposer.setSize(w, h);

      // Render Pass
      const renderPass = new RenderPass(scene, camera);
      renderPass.clearAlpha = 0;
      effectComposer.addPass(renderPass);

      // Unreal Bloom Pass
      const unrealBloomPass = new UnrealBloomPass();
      unrealBloomPass.strength = 0.5;
      unrealBloomPass.radius = 0;
      unrealBloomPass.threshold = 0;
      unrealBloomPass.enabled = false;
      effectComposer.addPass(unrealBloomPass);

      // Questi due pass devono essere necessariamente gli ultimi
      // Gamma Correction Pass
      const gammaCorrectionPass = new ShaderPass(GammaCorrectionShader);
      gammaCorrectionPass.enabled = false;
      effectComposer.addPass(gammaCorrectionPass);

      // Antialias fallback
      if (samples === 0) {
        const smaa = new SMAAPass(w, h); // <- real dimensions!
        effectComposer.addPass(smaa);
        smaaRef.current = smaa;
      } else {
        smaaRef.current = null;
      }

      composerRef.current = effectComposer;
    }

    // Gestisci il resize anche per il composer
    // Handle resize: keep SSAA and re-align SMAA
    const onResize = () => {
      const newBase = gl.getSize(new Vector2());
      const newW = Math.floor(newBase.x * ssaaRef.current);
      const newH = Math.floor(newBase.y * ssaaRef.current);
      composerRef.current.setSize(newW, newH);

      if (smaaRef.current) {
        // Some versions of SMAAPass don't have setSize – recreate if needed:
        composerRef.current.removePass(smaaRef.current);
        const newSmaa = new SMAAPass(newW, newH);
        composerRef.current.addPass(newSmaa);
        smaaRef.current = newSmaa;
      }
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [glRef.current, sceneRef.current, cameraRef.current]);

  // Gestione errori di caricamento modello
  const handleModelError = (errorMessage) => {
    console.error("Errore nel modello:", errorMessage);
    setError(errorMessage);
  };

  //   // Gestione delle animazioni disponibili nel modello
  //   const handleHasAnimations = (available, name, duration) => {
  //     if (available) {
  //       setHasAnimations(true);
  //       setIsFirstAnimation(true);
  //       // console.log('duration', duration);
  //       setAnimationDuration(duration);

  //       // Imposta il titolo dell'animazione in base al nome
  //       if (name === "explosion_glasses_an_1") {
  //         setAnimationTitle("EXPAND");
  //       } else if (name === "CO_FULL_AN_2") {
  //         setAnimationTitle("APPLY CLIP-ONS");
  //       } else if (name === "IL_FULL_AN_1") {
  //         setAnimationTitle("CHANGE LENSES");
  //       } else {
  //         setAnimationTitle("APPLY CLIP-ONS");
  //       }
  //     }
  //   };

  //   // Avvia l'animazione e gestisce il suo ciclo
  //   const startAnimation = async () => {
  //     setIsAnimatedOn(true);
  //     setTimeout(() => {
  //       setIsAnimatedOn(false);
  //       setIsFirstAnimation((alreadyAnimated) => !alreadyAnimated);
  //       // console.log('TimeOut!')
  //     }, animationDuration * 1000);
  //   };

  // Funzione per effettuare un render manuale e scaricare uno screenshot
  const handleRenderClick = async (filename, width, height, options = {}) => {
    const gl = glRef.current;
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const composer = composerRef.current;

    console.log(
      "handleRenderClick",
      filename,
      width,
      height,
      JSON.stringify(options)
    );

    const {
      download = false,
      cameraPosition,
      cameraRotation,
      cameraFov,
      cameraLookAt,
    } = options;

    if (gl && scene && camera && composer) {
      // Imposta nuove dimensioni per il render ad alta qualità
      const hiW = Math.floor(width * ssaaRef.current);
      const hiH = Math.floor(height * ssaaRef.current);

      gl.setSize(hiW, hiH);
      gl.setPixelRatio(1);
      // Imposta nuove dimensioni anche al composer
      composer.setSize(hiW, hiH);
      composer.setPixelRatio(1);

      if (smaaRef.current) {
        composer.removePass(smaaRef.current);
        const newSmaa = new SMAAPass(hiW, hiH);
        composer.addPass(newSmaa);
        smaaRef.current = newSmaa;
      }

      // Aggiorna l'aspect ratio della camera in base alle nuove dimensioni
      camera.aspect = hiW / hiH;
      // Aggiorna la posizione e la rotazione della camera
      if (cameraPosition !== undefined) {
        camera.position.set(
          cameraPosition[0],
          cameraPosition[1],
          cameraPosition[2]
        );
      } else {
        camera.position.set(0.392, 0.24, 0.642);
      }
      if (cameraRotation !== undefined) {
        camera.rotation.set(
          cameraRotation[0] * DEG_2_RAD,
          cameraRotation[1] * DEG_2_RAD,
          cameraRotation[2] * DEG_2_RAD
        );
      } else {
        camera.rotation.set(
          -15.39 * DEG_2_RAD,
          31.2 * DEG_2_RAD,
          6.4 * DEG_2_RAD
        );
      }

      // cambia la fov della camera
      if (cameraFov !== undefined) {
        camera.fov = cameraFov;
      } /*else {
        // Aggiorna il FOV in base al modello
        const fovCoeff = (4 * modelSize) / Math.PI;
        camera.fov = 10 - 10 * fovCoeff;
        // camera.fov = 9.5; // con clip-on su pavimento
      }*/
      console.log("Render camera fov:", camera.fov);

      if (cameraLookAt !== undefined) {
        camera.lookAt(cameraLookAt[0], cameraLookAt[1], cameraLookAt[2]);
      } else {
        camera.lookAt(-0.006, -0.016, -0.006);
        // camera.lookAt(center.x - 0.006, center.y - 0.0235, center.z); // con clip-on su pavimento
      }
      camera.updateProjectionMatrix();

      // se il parametro reflect è true, rende l'immagine dopo 50 ms per vedere il riflesso senza distorsione
      if (SCENE_PARAMS.useReflect) {
        // Usa Promise per gestire il timeout correttamente
        const finalDataURL = await new Promise((resolve) => {
          setTimeout(() => {
            const dataURL = renderImage(hiW, hiH, download, filename);
            resolve(dataURL);
          }, 50);
        });
        return { filename: filename, dataURL: finalDataURL };
      } else {
        const finalDataURL = renderImage(hiW, hiH, download, filename);
        return { filename: filename, dataURL: finalDataURL };
      }
    }

    return null;
  };

  const renderImage = (width, height, download, filename) => {
    const gl = glRef.current;
    const scene = sceneRef.current;
    const composer = composerRef.current;

    console.log("Rendering scene with EffectComposer...");
    // Renderizza la scena con l'effect composer
    if (SCENE_PARAMS.useReflect) {
      // PRIMO RENDER: con background pieno per aggiornare il riflesso
      composer.render();

      // SECONDO RENDER: con background trasparente per esportazione PNG
      scene.background = null;
      composer.render();
    } else {
      composer.render();
    }

    // Estrae l'immagine dal canvas
    let finalDataURL;
    if (ssaaRef.current > 1) {
      const srcCanvas = gl.domElement; // hi-res
      const outCanvas = document.createElement("canvas");
      outCanvas.width = width;
      outCanvas.height = height;
      const ctx = outCanvas.getContext("2d");
      if (!ctx) return null;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(srcCanvas, 0, 0, srcCanvas.width, srcCanvas.height, 0, 0, width, height);
      finalDataURL = outCanvas.toDataURL("image/png");
    } else {
      // DPR=1 e canvas già a misura finale
      finalDataURL = gl.domElement.toDataURL("image/png");
    }

    // se download è true, scarica l'immagine
    if (download) {
      console.log("Saving file...");
      // Converti il dataURL in Blob
      function dataURLtoBlob(dataurl) {
        const arr = dataurl.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], { type: mime });
      }

      const blob = dataURLtoBlob(finalDataURL);
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.download = filename;
      link.href = url;
      link.click();

      // Libera la memoria dopo un po'
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    }

    if (SCENE_PARAMS.useReflect) {
      // Ripristina il background se è reflect
      scene.background = new Color(SCENE_PARAMS.backgroundColor);
    }

    return finalDataURL;
  };

  // Render del componente
  return (
    <>
      {/* Schermata di caricamento */}
      <LoadingScreen progress={loadingProgress} isLoaded={isLoaded} />
      {/* Schermata di errore */}
      {error && <ErrorScreen message={error} />}

      {/* Contenitore principale della scena */}
      <div
        style={{
          height: props.singleView ? "100%" : "83%",
          width: "100%",
          backgroundColor: SCENE_PARAMS.useReflect ? "transparent" : SCENE_PARAMS.backgroundColor,
          backgroundSize: "cover",
          backgroundImage: SCENE_PARAMS.isBackgroundImage
            ? `url(${SCENE_PARAMS.backgroundImage})`
            : "",
        }}
      >
        {/* Canvas 3D con React Three Fiber */}
        <Canvas
          dpr={[1, 1]}
          camera={{
            position: [0, 0.125, 0.47],
            fov:
              window.innerWidth <= 700 && !props.isRenderer
                ? mobileFov
                : SCENE_PARAMS.fieldOfView,
            near: 0.05,
            far: 30,
          }}
          gl={{
            alpha: true,
            antialias: false, // <- irrelevant for the pass; better disable it
            stencil: false,
            depth: true,
            powerPreference: "high-performance",
            preserveDrawingBuffer: true, // <- useful for toDataURL
          }}
          onCreated={({ gl, scene, camera }) => {
            gl.xr.enabled = false;
            gl.shadowMap.enabled = true;
            gl.shadowMap.type = PCFSoftShadowMap;
            gl.setClearColor(0x000000, 0); // ← fondo trasparente
            gl.setClearAlpha(0);
            gl.outputColorSpace = SRGBColorSpace;
            gl.toneMapping = ACESFilmicToneMapping;
            gl.toneMappingExposure = 1.0;
            glRef.current = gl;
            sceneRef.current = scene;
            cameraRef.current = camera;
          }}
          shadows={!SCENE_PARAMS.useNoShadows}
        >
          {/* Visualizzazione della scena 3D */}
          {!error && SCENE_PARAMS.src && (
            <Suspense fallback={null}>
              <Stage
                adjustCamera={false} // Non modificare la camera
                shadows={
                  SCENE_PARAMS.useNoShadows
                    ? false
                    : {
                        // Configurazione delle ombre
                        type: "contact",
                        intensity: SCENE_PARAMS.shadowIntensity,
                        smooth: SCENE_PARAMS.shadowSmooth,
                      }
                }
                preset="rembrandt" // Preset per l'illuminazione
                environment={
                  SCENE_PARAMS.hdr
                    ? {
                        // Configurazione dell'ambiente
                        files: SCENE_PARAMS.hdr,
                        environmentIntensity:
                          SCENE_PARAMS.lightExposition - LIGHT_INTESITY_FACTOR,
                        environmentRotation: [0, Math.PI / 2, 0],
                      }
                    : {
                        preset: "apartment",
                        environmentIntensity: 0.9,
                        // environmentRotation: [Math.PI / 4, Math.PI / 4, Math.PI / 4],
                      }
                }
                intensity={0.1} // Intensità dell'illuminazione, default 0.5
              >
                <ModelLight
                  url={SCENE_PARAMS.src}
                  onError={handleModelError}
                  //   handleHasAnimations={handleHasAnimations}
                  //   isFirstAnimation={isFirstAnimation}
                  //   isAnimatedOn={isAnimatedOn}
                  setModelInfo={setModelInfo}
                />
              </Stage>
              {/* Piano riflettente (opzionale) */}
              {SCENE_PARAMS.useReflect && (
                <>
                {!SCENE_PARAMS.backgroundImage && <color attach="background" args={[SCENE_PARAMS.backgroundColor]}/>}
                  <mesh
                    position={[0, SCENE_PARAMS.modelMinY - 0.0005, 0]}
                    rotation={[-Math.PI / 2, 0, 0]}
                    receiveShadow
                    renderOrder={-1}
                  >
                    <planeGeometry args={[10, 10]} />
                    <MeshReflectorMaterial
                      blur={[0, 0]} // Blur ground reflections (width, height), 0 skips blur
                      mixBlur={0.01} // How much blur mixes with surface roughness (default = 1)
                      mixStrength={2048} // Strength of the reflections
                      mixContrast={1} // Contrast of the reflections
                      resolution={1024} // Off-buffer resolution, lower=faster, higher=better quality, slower
                      mirror={0} // Mirror environment, 0 = texture colors, 1 = pick up env colors
                      depthScale={0.1} // Scale the depth factor (0 = no depth, default = 0)
                      minDepthThreshold={0.8} // Lower edge for the depthTexture interpolation (default = 0)
                      maxDepthThreshold={0.91} // Upper edge for the depthTexture interpolation (default = 0)
                      depthToBlurRatioBias={0.05} // Adds a bias factor to the depthTexture before calculating the blur amount [blurFactor = blurTexture * (depthTexture + bias)]. It accepts values between 0 and 1, default is 0.25. An amount > 0 of bias makes sure that the blurTexture is not too sharp because of the multiplication with the depthTexture
                      reflectorOffset={0.001} // Offsets the virtual camera that projects the reflection. Useful when the reflective surface is some distance from the object's origin (default = 0)
                      transparent
                      opacity={0.1}
                      metalness={0}
                      roughness={0.72}
                      color="#030303"
                      depthWrite={false}
                      depthTest
                      renderOrder={-1}
                    />
                  </mesh>
                </>
              )}
            </Suspense>
          )}
        </Canvas>

        {/* Overlay animazione */}
        <div style={{ position: "relative" }}>
          {/* Bottone per avviare l'animazione */}
          {/* {hasAnimations && (
            <div
              className={styles.animationContainer}
              style={{ display: isAnimatedOn ? "none" : "block" }}
            >
              <Button
                onClick={startAnimation}
                className={styles.animation_button}
                icon={
                  <img
                    src={playIcon}
                    className={styles.clipped_icon_button}
                    alt="Play"
                  />
                }
              >
                {isFirstAnimation ? animationTitle : comeBackTitle}
              </Button>
            </div>
          )} */}
        </div>
      </div>
    </>
  );
});

export default ThreeJSViewerLight;
